import { IDestroyOptions, isMobile, Sprite, Texture, Ticker } from "pixi.js";
import { PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { GameController } from "../controllers/GameController";
import { AdaptiveView } from "../../../common/views/AdaptiveView";
import { CoinsCounter } from "../../../common/components/CoinsCounter";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { HealthBar } from "../components/2d/HealthBar";
import { BoostersPanel } from "../components/2d/BoostersPanel";
import { PlayerModel } from "../../../common/models/PlayerModel";
import { BoosterType } from "../../../common/models/data/BoosterType";
import { GameResourcesPackage } from "../resources/GameResourcesPackage";
import { GameView3d } from "./GameView3d";
import { AdMultiplierComponent } from "../../../common/components/AdMultiplierComponent";
import { nameof } from "bindable-data";
import { GameModel } from "../models/GameModel";
import { GameViewModel } from "../models/GameViewModel";
import { WeaponType } from "../models/data/WeaponType";
import { GameState } from "../models/data/GameState";
import { Easing, Tween } from "@tweenjs/tween.js";
import { SpineResourcesPackage } from "../../../common/resources/SpineResourcesPackage";
import { WeaponsPanel } from "../components/2d/weapons/WeaponsPanel";
import { LevelView } from "../../menu/components/LevelView";
import { SettingsModel } from "../../../common/models/SettingsModel";
import { LocalizationModel } from "../../../common/models/LocalizationModel";
import { CancellationToken } from "../../../../composition/CancellationToken";
import { TimeLeftComponent } from "../components/2d/TimeLeftComponent";
import { UIButtonBase } from "../../../common/components/UIButtonBase";
import { BoxReward } from "../components/3d/gamefield/box/BoxReward";
import { AudioController } from "../components/AudioController";
import { GameSceneOptions } from "../GameSceneOptions";
import { FullScreenInputBlocker } from "../../../common/components/inputBlocker/FullScreenInputBlocker";

export class GameView extends AdaptiveView<void> {
    private currentLevel: LevelView;
    private coinsCounter: CoinsCounter;
    private pauseButton: UIButtonBase;
    private healthBar: HealthBar;
    private adMultiplier: AdMultiplierComponent;
    private boostersPanel: BoostersPanel;
    private weaponsPanel: WeaponsPanel;
    private timerComponent: TimeLeftComponent;
    private paused: boolean = false;
    private cancel: CancellationToken;

    public constructor(
        private readonly commonResources: CommonResourcesPackage,
        private readonly gameResources: GameResourcesPackage,
        private readonly spineResources: SpineResourcesPackage,
        private readonly playerModel: PlayerModel,
        private readonly settingsModel: SettingsModel,
        private readonly localizationModel: LocalizationModel,
        private readonly gameModel: GameModel,
        private readonly gameViewModel: GameViewModel,
        private readonly gameController: GameController,
        private readonly audioController: AudioController,
        private readonly gameView3d: GameView3d,
        canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(canvasScaler, operationControls);
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);

        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleOrientationChange = this.handleOrientationChange.bind(this);
        window.addEventListener("blur", this.handleVisibilityChange);
        window.addEventListener("orientationchange", this.handleOrientationChange);
        document.addEventListener("visibilitychange", this.handleVisibilityChange);

        this.gameViewModel.spawnBooster.add(this.onBoosterSpawn, this);
        this.gameViewModel.spawnCoin.add(this.onCoinSpawn, this);
        this.gameViewModel.spawnHealth.add(this.onHealthSpawn, this);
        this.gameViewModel.spawnWeapon.add(this.onWeaponSpawn, this);

        this.playerModel.propertyChanged.add(this.onPlayerModelPropertyChange, this);
        this.adMultiplier.onAdClick.add(this.onAdClick, this);
        this.adMultiplier.timeout.add(this.onAdTimeout, this);
        this.weaponsPanel.onWeaponClick.add(this.onWeaponClick, this);
        this.boostersPanel.onBoosterClick.add(this.onBoosterClick, this);
        this.pauseButton.onUp.add(this.onPauseClick, this);
        this.cancel = new CancellationToken();
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.cancel.cancel();
        window.removeEventListener("blur", this.handleVisibilityChange);
        window.removeEventListener("orientationchange", this.handleOrientationChange);
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        this.gameViewModel.spawnBooster.remove(this.onBoosterSpawn, this);
        this.gameViewModel.spawnCoin.remove(this.onCoinSpawn, this);
        this.gameViewModel.spawnHealth.remove(this.onHealthSpawn, this);
        this.gameViewModel.spawnWeapon.remove(this.onWeaponSpawn, this);
        this.playerModel.propertyChanged.remove(this.onPlayerModelPropertyChange, this);
        this.adMultiplier.onAdClick.remove(this.onAdClick, this);
        this.adMultiplier.timeout.remove(this.onAdTimeout, this);
        this.weaponsPanel.onWeaponClick.remove(this.onWeaponClick, this);
        this.boostersPanel.onBoosterClick.remove(this.onBoosterClick, this);
        this.pauseButton.onUp.remove(this.onPauseClick, this);
        super.destroy(options);
    }

    public start(): void {
        this.gameController.start();
    }

    private build(): void {
        this.currentLevel = new LevelView(this.settingsModel, this.localizationModel, this.playerModel, this.commonResources);
        this.coinsCounter = new CoinsCounter(this.playerModel, this.commonResources, this.spineResources);
        this.pauseButton = new UIButtonBase({ defaultTexture: this.gameResources.assets_game_ui_pause_button_png });
        this.healthBar = new HealthBar(this.commonResources, this.gameResources, this.gameView3d.Character);
        this.adMultiplier = new AdMultiplierComponent(this.commonResources, "x 2");
        this.boostersPanel = new BoostersPanel(this.commonResources, this.playerModel);
        this.weaponsPanel = new WeaponsPanel(this.commonResources, this.gameView3d.Character);
        this.timerComponent = new TimeLeftComponent(this.gameResources, this.gameModel, this.audioController);

        this.addChild(
            this.currentLevel,
            this.coinsCounter,
            this.pauseButton,
            this.healthBar,
            this.adMultiplier,
            this.boostersPanel,
            this.weaponsPanel,
            this.timerComponent
        );
    }

    protected layout(width: number, height: number): void {
        switch (height) {
            case 2400:
                this.layout9x20(width);
                break;
            case 1620:
                this.layout10x9(width);
                break;
            case 1800:
                this.layout4x3(width);
                break;
        }
    }

    private layout9x20(width: number): void {
        this.boostersPanel.changeOrientation("horizontal");
        this.weaponsPanel.changeOrientation("horizontal");
        this.currentLevel.position.set((this.canvasScaler.referenceWidth - this.currentLevel.width) * 0.5, 40);
        this.coinsCounter.scale.set(0.85);
        this.coinsCounter.position.set(728.725 + this.coinsCounter.width * 0.5, 60 + this.coinsCounter.height * 0.5);
        this.pauseButton.scale.set(0.85);
        this.pauseButton.position.set(900, 200);
        this.healthBar.scale.set(0.85);
        this.healthBar.position.set(0, 60);
        this.adMultiplier.scale.set(0.85);
        this.adMultiplier.position.set(65, 255);
        this.weaponsPanel.scale.set(0.85);
        this.weaponsPanel.position.set((width - this.weaponsPanel.width) * 0.5, 1840);
        this.boostersPanel.scale.set(0.85);
        this.boostersPanel.position.set((width - this.boostersPanel.width) * 0.5, 2020);
        this.timerComponent.position.set((width - this.timerComponent.width) * 0.5, 2285);
    }

    private layout10x9(width: number): void {
        this.boostersPanel.changeOrientation("vertical");
        this.weaponsPanel.changeOrientation("vertical");
        this.currentLevel.position.set((this.canvasScaler.referenceWidth - this.currentLevel.width) * 0.5, 100);
        this.coinsCounter.position.set(150 + this.coinsCounter.width * 0.5, 100 + this.coinsCounter.height * 0.5);
        this.healthBar.position.set(150, 250);
        this.adMultiplier.position.set(230, 460);
        this.pauseButton.position.set(width - this.pauseButton.width - 185, 100);
        this.boostersPanel.position.set(width - this.boostersPanel.width - 180, 450);
        this.weaponsPanel.position.set(230, 650);
        this.timerComponent.position.set((width - this.timerComponent.width) * 0.5, this.canvasScaler.referenceHeight - 75);
    }

    private layout4x3(width: number): void {
        this.boostersPanel.changeOrientation("vertical");
        this.weaponsPanel.changeOrientation("vertical");
        this.currentLevel.position.set((this.canvasScaler.referenceWidth - this.currentLevel.width) * 0.5, 100);
        this.coinsCounter.position.set(150 + this.coinsCounter.width * 0.5, 100 + this.coinsCounter.height * 0.5);
        this.healthBar.position.set(150, 250);
        this.adMultiplier.position.set(230, 460);
        this.pauseButton.position.set(width - this.pauseButton.width - 185, 100);
        this.boostersPanel.position.set(width - this.boostersPanel.width - 180, 450);
        this.weaponsPanel.position.set(230, 650);
        this.timerComponent.position.set((width - this.timerComponent.width) * 0.5, this.canvasScaler.referenceHeight - 75);
    }

    private async handleVisibilityChange(): Promise<void> {
        await this.onPauseClick();
    }

    private handleOrientationChange(): void {
        this.onPauseClick();
        requestAnimationFrame(() => {
            isMobile.phone && !window.isPortrait() && this.gameController.showForceRotatePopup();
        });
    }

    private async onBoosterClick(type: BoosterType, isAdRefill: boolean): Promise<void> {
        if (this.gameModel.state.value !== GameState.Playing) {
            return;
        }
        if (isAdRefill) {
            await this.boosterForAdClick(type);
        } else {
            switch (type) {
                case BoosterType.Energy: {
                    if (!this.gameView3d.canApplyEnergy) {
                        return;
                    }
                    break;
                }
                case BoosterType.Freeze: {
                    if (!this.gameView3d.canApplyFreeze) {
                        return;
                    }
                    break;
                }
                case BoosterType.Mine: {
                    if (!this.gameView3d.canPlaceMines) {
                        return;
                    }
                    break;
                }
                case BoosterType.Turret: {
                    if (!this.gameView3d.canPlaceTurret) {
                        return;
                    }
                    break;
                }
            }
            this.gameController.subtractBooster(type);
        }
    }

    private async boosterForAdClick(type: BoosterType): Promise<void> {
        const inputBlocker = new FullScreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        const adResult = await this.gameController.showAd();
        if (adResult) {
            this.gameController.addBooster(type);
        } else {
            console.warn("GameView.boosterForAdClick player don't see ad!");
        }
        this.removeChild(inputBlocker);
    }

    private onWeaponClick(weapon: WeaponType): void {
        this.gameView3d.Character.changeWeapon(weapon);
    }

    private async onPauseClick(): Promise<void> {
        if (this.paused) {
            return;
        }
        this.paused = true;
        const restartGameSceneOptions: GameSceneOptions = {
            initialHousesRotation: this.gameView3d.initialHousesRotation,
        };
        await this.gameController.showPausePopup(this.operationControls, restartGameSceneOptions);
        this.paused = false;
    }

    private onPlayerModelPropertyChange(model: PlayerModel, key: string): void {
        if (key === nameof(model, "lastBoxReward")) {
            this.adMultiplier.resetTimer();
            if (model.lastBoxReward.value !== null) {
                this.adMultiplier.startTimer();
            }
        }
    }

    private async onAdClick(): Promise<void> {
        if (this.gameModel.state.value !== GameState.Playing) {
            return;
        }
        const inputBlocker = new FullScreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        const adResult = await this.gameController.showAd();
        if (adResult) {
            this.gameController.applyDoubleBoxReward();
        } else {
            console.warn("GameView.onAdClick player don't see ad!");
        }
        this.removeChild(inputBlocker);
    }

    private onAdTimeout(): void {
        this.adMultiplier.resetTimer();
        this.gameController.clearBoxReward();
    }

    private async onBoosterSpawn(type: BoosterType, count: number, index: number, doubleReward: BoxReward): Promise<void> {
        const initialCoords = this.toLocal(this.gameView3d.getScreenCoordinatesByFieldIndex(index));
        const resultCoords = this.boostersPanel.getFlyTargetCoordinates(type);
        let texture;
        switch (type) {
            case BoosterType.Mine: {
                texture = this.commonResources.assets_common_boosters_mine_png;
                break;
            }
            case BoosterType.Energy: {
                texture = this.commonResources.assets_common_boosters_energy_png;
                break;
            }
            case BoosterType.Freeze: {
                texture = this.commonResources.assets_common_boosters_freeze_png;
                break;
            }
            case BoosterType.Turret: {
                texture = this.commonResources.assets_common_boosters_turret_png;
                break;
            }
        }

        await this.textureFly(texture, initialCoords, resultCoords, 1000, { initialScale: this.interpolateByIndex(index), targetScale: 1.2 });
        if (!this.cancel.isCancellationRequested) {
            this.gameController.addBooster(type, count);
            this.gameController.setDoubleBoxReward(doubleReward);
        }
    }

    private async onCoinSpawn(value: number, index: number, doubleReward?: BoxReward): Promise<void> {
        const initialCoords = this.toLocal(this.gameView3d.getScreenCoordinatesByFieldIndex(index));
        const resultCoords = this.coinsCounter.coinFlyTargetCoordinates;
        await this.textureFly(this.commonResources.assets_common_coin_icon_png, initialCoords, resultCoords, 1000, { initialScale: this.interpolateByIndex(index), targetScale: 1.2 });
        this.gameController.addCoins(value);
        await this.coinsCounter.addCoins(value);
        if (doubleReward) {
            this.gameController.setDoubleBoxReward(doubleReward);
        }
    }

    private async onHealthSpawn(value: number, index: number, doubleReward?: BoxReward): Promise<void> {
        const initialCoords = this.toLocal(this.gameView3d.getScreenCoordinatesByFieldIndex(index));
        const resultCoords = this.healthBar.flyTargetCoordinates;
        await this.textureFly(this.gameResources.assets_game_ui_health_bar_fly_health_icon_png, initialCoords, resultCoords);
        if (this.gameView3d.Character.alive) {
            this.gameController.addCharacterHealth(value);
            this.gameController.setDoubleBoxReward(doubleReward);
        }
    }

    private async onWeaponSpawn(type: WeaponType, count: number, index: number, doubleReward: BoxReward): Promise<void> {
        const initialCoords = this.toLocal(this.gameView3d.getScreenCoordinatesByFieldIndex(index));
        const resultCoords = this.weaponsPanel.getFlyTargetCoordinates(type);
        let texture;
        switch (type) {
            case WeaponType.Dynamite: {
                texture = this.commonResources.assets_common_weapons_dynamite_png;
                break;
            }
            case WeaponType.Boomerang: {
                texture = this.commonResources.assets_common_weapons_boomerang_png;
                break;
            }
            default: {
                console.warn(`GameView.onWeaponSpawn try to spawn unhandled texture of type=${type}`);
            }
        }
        await this.textureFly(texture, initialCoords, resultCoords, 1000, { initialScale: 0.45, targetScale: 0.85 });
        this.gameView3d.Character.addWeapon(type, count);
        this.gameController.setDoubleBoxReward(doubleReward);
    }

    private async textureFly(
        texture: Texture,
        initialCoords: { x: number; y: number },
        resultCoords: { x: number; y: number },
        durationMS: number = 750,
        scale: { initialScale: number; targetScale: number } = { initialScale: 0.5, targetScale: 1 },
        arcHeight: number = -190,
    ): Promise<{ cancel: () => void }> {
        return new Promise((resolve) => {
            const { initialScale, targetScale } = scale;
            const sprite = new Sprite(texture);
            sprite.anchor.set(0.5);
            sprite.position.set(initialCoords.x, initialCoords.y);
            sprite.scale.set(initialScale);
            this.addChild(sprite);

            const cleanup = () => {
                Ticker.shared.remove(scaleUpdate, this);
                Ticker.shared.remove(movementUpdate, this);
                if (sprite.parent) this.removeChild(sprite);
                sprite.destroy();
            };


            const cancel = () => {
                cleanup();
                resolve({ cancel });
            };
            this.cancel.onCanceled.add(cancel, this);

            // Phase 1: scale animation
            const scaleTween = new Tween({ s: 0 })
                .to({ s: initialScale }, durationMS * 0.4)
                .easing(Easing.Cubic.Out)
                .onUpdate((obj) => {
                if (!this.cancel.isCancellationRequested) sprite.scale.set(obj.s);
            })
            .onComplete(() => {
                if (this.cancel.isCancellationRequested) return;
                Ticker.shared.remove(scaleUpdate, this);

                // Phase 2: movement animation
                const moveTween = new Tween({ t: 0, s: initialScale })
                    .to({ t: 1, s: targetScale }, durationMS * 0.6)
                    .easing(Easing.Cubic.Out)
                    .onUpdate((obj) => {
                        if (this.cancel.isCancellationRequested) return;
                        const progress = obj.t;

                        // X moves linearly
                        sprite.x = initialCoords.x + (resultCoords.x - initialCoords.x) * progress;

                        // Y follows parabolic arc
                        const baseY = initialCoords.y + (resultCoords.y - initialCoords.y) * progress;
                        const arc = Math.sin(progress * Math.PI) * arcHeight;
                        sprite.y = baseY - arc;

                        // scale interpolates
                        sprite.scale.set(obj.s);
                    })
                    .onComplete(() => {
                        if (this.cancel.isCancellationRequested) return;
                        cleanup();
                        this.cancel.onCanceled.remove(cancel, this);
                        resolve({ cancel });
                    });

                movementUpdate = () => moveTween.update();
                Ticker.shared.add(movementUpdate, this);
                moveTween.start();
            });

            let scaleUpdate = () => scaleTween.update();
            let movementUpdate: () => void = () => { };

            Ticker.shared.add(scaleUpdate, this);
            scaleTween.start();
        });
    }

    private interpolateByIndex(index: number, minResult: number = 0.4, maxResult: number = 0.7): number {
        const tens = Math.floor(index / 10);

        // normalize tens into [0,1]
        const t = tens / 8;

        // lerp between maxResult (when tens=0) and minResult (when tens=8)
        return maxResult + (minResult - maxResult) * t;
    }
}
